/**
 * Adapted from PlayCanvas Engine's gsplat-octree.js / gsplat-octree-node.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type { GSplatPlacementResource } from '@/plugins/gsplat/unified/GSplatPlacement'
import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import type { GSplatAssetLoaderBase } from './GSplatAssetLoaderBase'

const log = getLoggerManager().getLogger('gsplat:octree')

export interface GSplatOctreeNodeLod {
  readonly file: string
  readonly fileIndex: number
  readonly offset: number
  readonly count: number
}

export interface GSplatOctreeBoundData {
  readonly min: readonly [number, number, number]
  readonly max: readonly [number, number, number]
}

export interface GSplatOctreeManifestLod {
  readonly file: number
  readonly offset?: number
  readonly count?: number
}

export interface GSplatOctreeManifestNode {
  readonly lods?: Readonly<Record<string, GSplatOctreeManifestLod>>
  readonly bound?: GSplatOctreeBoundData
  readonly children?: readonly GSplatOctreeManifestNode[]
}

export interface GSplatOctreeManifest {
  readonly lodLevels: number
  readonly filenames: readonly string[]
  readonly tree: GSplatOctreeManifestNode
  readonly environment?: string
}

export interface GSplatOctreeFileResource extends Disposable, GSplatPlacementResource {
  readonly refCount?: number
  readonly generation?: number
  readonly numSplats?: number
  readonly sourceGpuBytes?: number
  releaseTextureSources?(): void
}

/** 当前 Octree 仍持有的唯一 source residency；不包含逐 View WorkBuffer/order/scratch。 */
export interface GSplatOctreeResidencySnapshot {
  readonly resourceCount: number
  readonly splatCount: number
  readonly gpuBytes: number
}

export interface GSplatOctreeUrlPolicy {
  resolve(assetFileUrl: string, fileUrl: string, fileIndex: number): string
}

export interface GSplatOctreeUrlPolicyOptions {
  readonly baseUrl?: string
  readonly baseUrls?: readonly string[]
}

/** PlayCanvas 默认相对路径规则与本地代理/CDN 分片扩展的独立 URL policy。 */
export class GSplatOctreePathUrlPolicy implements GSplatOctreeUrlPolicy {
  private readonly baseUrl?: string
  private readonly baseUrls?: readonly string[]

  constructor(options: GSplatOctreeUrlPolicyOptions = {}) {
    this.baseUrl = options.baseUrl
    this.baseUrls = options.baseUrls ? [...options.baseUrls] : undefined
    for (const [index, value] of this.baseUrls?.entries() ?? []) {
      if (!value) throw new Error(`GSplatOctree baseUrls 不能为空: index=${index}`)
    }
  }

  resolve(assetFileUrl: string, fileUrl: string, fileIndex: number): string {
    if (!isRelativeUrl(fileUrl)) return fileUrl
    const mainBase = this.baseUrl ?? resolveBaseUrl(assetFileUrl)
    if (fileIndex < 0 || !this.baseUrls || this.baseUrls.length === 0) {
      return joinPath(mainBase, fileUrl)
    }
    const shard = this.baseUrls[fileIndex % this.baseUrls.length]
    return joinPath(normalizeShardDir(shard, mainBase), fileUrl)
  }
}

export interface GSplatLogicalFrameToken {
  readonly source: object
  readonly value: number
}

export interface GSplatOctreeOptions<TResource extends GSplatOctreeFileResource> {
  readonly loader?: GSplatAssetLoaderBase<TResource>
  readonly urlPolicy?: GSplatOctreeUrlPolicy
  readonly loadEnvironment?: boolean
  readonly cooldownTicks?: number
  /** 有值时拒绝发布 generation 不匹配的 late/context-lost Resource。 */
  readonly resourceGeneration?: number
  readonly traceLodCounts?: (lodCounts: readonly number[]) => void
}

export interface GSplatOctreeLoaderReplacement {
  commit(): void
  rollback(): void
  finalize(): void
}

export class GSplatOctreeNode {
  readonly bounds: THREE.Box3
  readonly boundingSphere: THREE.Vector4

  constructor(
    readonly lods: GSplatOctreeNodeLod[],
    boundData: GSplatOctreeBoundData
  ) {
    validateBound(boundData)
    this.bounds = new THREE.Box3(
      new THREE.Vector3(...boundData.min),
      new THREE.Vector3(...boundData.max)
    )
    const center = this.bounds.getCenter(new THREE.Vector3())
    const halfExtents = this.bounds.getSize(new THREE.Vector3()).multiplyScalar(0.5)
    this.boundingSphere = new THREE.Vector4(center.x, center.y, center.z, halfExtents.length())
  }
}

const defaultUrlPolicy = new GSplatOctreePathUrlPolicy()

/** 共享 metadata、file residency、ref/load-interest 与 cooldown 的 Octree owner。 */
export class GSplatOctree<TResource extends GSplatOctreeFileResource> implements Disposable {
  readonly nodes: GSplatOctreeNode[]
  readonly nodeBoundsMinMax: Float32Array
  readonly files: Array<{ url: string; lodLevel: number }>
  readonly lodLevels: number
  readonly assetFileUrl: string
  readonly fileResources = new Map<number, TResource>()
  readonly fileRefCounts: Int32Array
  readonly fileLoadInterests: Int32Array
  readonly cooldowns = new Map<number, number>()
  readonly environmentUrl: string | null
  environmentResource: TResource | null = null
  environmentRefCount = 0

  private loaderValue: GSplatAssetLoaderBase<TResource> | null
  private readonly restoringFileResources = new Map<number, TResource>()
  private restoringEnvironmentResource: TResource | null = null
  private cooldownTicksValue: number
  private resourceGenerationValue: number | undefined
  private readonly traceLodCountsCallback?: (lodCounts: readonly number[]) => void
  private lastCooldownToken: GSplatLogicalFrameToken | null = null
  private restoringValue = false
  private destroyed = false

  constructor(
    assetFileUrl: string,
    manifest: GSplatOctreeManifest,
    options: GSplatOctreeOptions<TResource> = {}
  ) {
    if (!assetFileUrl) throw new Error('GSplatOctree assetFileUrl 不能为空')
    if (!Number.isSafeInteger(manifest.lodLevels) || manifest.lodLevels < 1) {
      throw new Error(`非法 GSplatOctree lodLevels: ${manifest.lodLevels}`)
    }
    this.assetFileUrl = assetFileUrl
    this.lodLevels = manifest.lodLevels
    const urlPolicy = options.urlPolicy ?? defaultUrlPolicy
    this.files = manifest.filenames.map((url, index) => {
      if (!url) throw new Error(`GSplatOctree filename 不能为空: ${index}`)
      return { url: urlPolicy.resolve(assetFileUrl, url, index), lodLevel: -1 }
    })
    this.fileRefCounts = new Int32Array(this.files.length)
    this.fileLoadInterests = new Int32Array(this.files.length)
    this.loaderValue = options.loader ?? null
    this.cooldownTicksValue = this.validateCooldown(options.cooldownTicks ?? 100)
    this.resourceGenerationValue = this.validateGeneration(options.resourceGeneration)
    this.traceLodCountsCallback = options.traceLodCounts
    this.environmentUrl =
      options.loadEnvironment === false || !manifest.environment
        ? null
        : urlPolicy.resolve(assetFileUrl, manifest.environment, -1)

    const leaves: Array<{
      lods: Readonly<Record<string, GSplatOctreeManifestLod>>
      bound: GSplatOctreeBoundData
    }> = []
    this.extractLeaves(manifest.tree, leaves)
    this.nodes = leaves.map((leaf, nodeIndex) => {
      const lods: GSplatOctreeNodeLod[] = []
      for (let lodIndex = 0; lodIndex < this.lodLevels; lodIndex++) {
        const input = leaf.lods[String(lodIndex)]
        if (!input) {
          lods.push({ file: '', fileIndex: -1, offset: 0, count: 0 })
          continue
        }
        this.validateManifestLod(input, nodeIndex, lodIndex)
        this.files[input.file].lodLevel = lodIndex
        lods.push({
          file: this.files[input.file].url,
          fileIndex: input.file,
          offset: input.offset ?? 0,
          count: input.count ?? 0,
        })
      }
      return new GSplatOctreeNode(lods, leaf.bound)
    })
    this.nodeBoundsMinMax = this.createFlatBounds(this.nodes)
  }

  get loader(): GSplatAssetLoaderBase<TResource> | null {
    return this.loaderValue
  }

  get isDestroyed(): boolean {
    return this.destroyed
  }

  get isRestoring(): boolean {
    return this.restoringValue
  }

  get hasPendingCooldowns(): boolean {
    return this.cooldowns.size > 0
  }

  get numSplats(): number {
    let result = 0
    for (const node of this.nodes) result += node.lods[0]?.count ?? 0
    return result
  }

  getResidencySnapshot(): GSplatOctreeResidencySnapshot {
    const resources = new Set<TResource>([
      ...this.fileResources.values(),
      ...this.restoringFileResources.values(),
    ])
    if (this.environmentResource) resources.add(this.environmentResource)
    if (this.restoringEnvironmentResource) resources.add(this.restoringEnvironmentResource)
    let splatCount = 0
    let gpuBytes = 0
    for (const resource of resources) {
      const count = resource.numSplats ?? 0
      const bytes = resource.sourceGpuBytes ?? 0
      if (!Number.isSafeInteger(count) || count < 0) {
        throw new Error(`非法 GSplat Octree resident splatCount: ${count}`)
      }
      if (!Number.isSafeInteger(bytes) || bytes < 0) {
        throw new Error(`非法 GSplat Octree resident source bytes: ${bytes}`)
      }
      splatCount += count
      gpuBytes += bytes
      if (!Number.isSafeInteger(splatCount) || !Number.isSafeInteger(gpuBytes)) {
        throw new Error('GSplat Octree residency 超出安全整数范围')
      }
    }
    return Object.freeze({ resourceCount: resources.size, splatCount, gpuBytes })
  }

  getFileResource(fileIndex: number): TResource | undefined {
    this.assertFileIndex(fileIndex)
    return this.fileResources.get(fileIndex)
  }

  ensureFileResource(fileIndex: number): TResource | undefined {
    this.assertFileIndex(fileIndex)
    if (this.destroyed || this.restoringValue || !this.loaderValue) return undefined
    const ready = this.fileResources.get(fileIndex)
    if (ready) return ready

    const url = this.files[fileIndex].url
    const loaded = this.loaderValue.getResource(url)
    if (!loaded) {
      this.loaderValue.load(url)
      return undefined
    }
    if (!this.isCurrentGeneration(loaded)) {
      this.loaderValue.unload(url)
      this.loaderValue.load(url)
      return undefined
    }
    this.fileResources.set(fileIndex, loaded)
    loaded.releaseTextureSources?.()
    if (this.fileRefCounts[fileIndex] === 0) {
      this.cooldowns.set(fileIndex, this.cooldownTicksValue)
    }
    this.traceLodCounts()
    return loaded
  }

  incRefCount(fileIndex: number): void {
    this.assertFileIndex(fileIndex)
    this.fileRefCounts[fileIndex]++
    this.cooldowns.delete(fileIndex)
  }

  decRefCount(fileIndex: number, cooldownTicks = this.cooldownTicksValue): void {
    this.assertFileIndex(fileIndex)
    if (this.fileRefCounts[fileIndex] === 0) {
      log.warn(`GSplatOctree file ref underflow prevented: fileIndex=${fileIndex}`)
      return
    }
    this.fileRefCounts[fileIndex]--
    if (this.fileRefCounts[fileIndex] !== 0) return
    const ticks = this.validateCooldown(cooldownTicks)
    if (ticks === 0) {
      if (!this.tryUnloadResource(fileIndex)) this.cooldowns.set(fileIndex, 1)
    } else this.cooldowns.set(fileIndex, ticks)
  }

  beginLoadInterest(fileIndex: number): void {
    this.assertFileIndex(fileIndex)
    this.fileLoadInterests[fileIndex]++
    this.cooldowns.delete(fileIndex)
  }

  incEnvironmentRefCount(): void {
    this.environmentRefCount++
  }

  decEnvironmentRefCount(): void {
    if (this.environmentRefCount === 0) {
      log.warn('GSplatOctree environment ref underflow prevented')
      return
    }
    this.environmentRefCount--
    if (this.environmentRefCount !== 0 || !this.environmentUrl || !this.loaderValue) return
    this.unloadEnvironmentResource()
  }

  ensureEnvironmentResource(): TResource | undefined {
    if (this.destroyed || this.restoringValue || !this.environmentUrl || !this.loaderValue) {
      return undefined
    }
    if (this.environmentResource) return this.environmentResource
    const loaded = this.loaderValue.getResource(this.environmentUrl)
    if (!loaded) {
      this.loaderValue.load(this.environmentUrl)
      return undefined
    }
    if (!this.isCurrentGeneration(loaded)) {
      this.loaderValue.unload(this.environmentUrl)
      this.loaderValue.load(this.environmentUrl)
      return undefined
    }
    this.environmentResource = loaded
    if (this.environmentRefCount === 0) {
      this.unloadEnvironmentResource()
      return undefined
    }
    return loaded
  }

  unloadEnvironmentResource(): void {
    try {
      if (this.loaderValue && this.environmentUrl) this.loaderValue.unload(this.environmentUrl)
    } finally {
      this.environmentResource = null
    }
  }

  endLoadInterest(fileIndex: number): void {
    this.assertFileIndex(fileIndex)
    if (this.fileLoadInterests[fileIndex] === 0) {
      log.warn(`GSplatOctree load-interest underflow prevented: fileIndex=${fileIndex}`)
      return
    }
    this.fileLoadInterests[fileIndex]--
    if (this.fileLoadInterests[fileIndex] === 0 && this.fileRefCounts[fileIndex] === 0) {
      this.cooldowns.set(fileIndex, this.cooldownTicksValue)
    }
  }

  updateCooldownTick(token: GSplatLogicalFrameToken, cooldownTicks: number): void {
    if (
      this.lastCooldownToken?.source === token.source &&
      this.lastCooldownToken.value === token.value
    ) {
      return
    }
    this.lastCooldownToken = token
    this.cooldownTicksValue = this.validateCooldown(cooldownTicks)
    this.pruneRestoringResources()
    for (const [fileIndex, remaining] of [...this.cooldowns]) {
      if (remaining > 1) {
        this.cooldowns.set(fileIndex, remaining - 1)
        continue
      }
      if (this.tryUnloadResource(fileIndex)) this.cooldowns.delete(fileIndex)
      else this.cooldowns.set(fileIndex, 1)
    }
  }

  unloadResource(fileIndex: number): void {
    this.assertFileIndex(fileIndex)
    this.tryUnloadResource(fileIndex, true)
    this.cooldowns.delete(fileIndex)
  }

  /**
   * Context restore 的 loader/resource generation 替换事务。stage 后旧 Resource 对新 WorldState 隐藏，
   * rollback 可恢复；finalize 才销毁旧 loader，已有 borrower 由 Resource 自身 lease 延迟释放。
   */
  stageLoaderReplacement(
    loader: GSplatAssetLoaderBase<TResource>,
    resourceGeneration?: number
  ): GSplatOctreeLoaderReplacement {
    if (this.destroyed) throw new Error('GSplatOctree 已销毁')
    if (this.restoringValue) throw new Error('GSplatOctree 已在恢复中')
    const nextGeneration = this.validateGeneration(resourceGeneration)
    const previousLoader = this.loaderValue
    const previousResources = new Map(this.fileResources)
    const previousEnvironment = this.environmentResource
    const previousCooldowns = new Map(this.cooldowns)
    const previousToken = this.lastCooldownToken
    const previousGeneration = this.resourceGenerationValue
    let committed = false
    let finalized = false

    this.restoringValue = true
    for (const [fileIndex, resource] of this.fileResources) {
      this.restoringFileResources.set(fileIndex, resource)
    }
    this.fileResources.clear()
    this.restoringEnvironmentResource = previousEnvironment
    this.environmentResource = null

    return {
      commit: () => {
        if (finalized || committed) return
        this.loaderValue = loader
        this.resourceGenerationValue = nextGeneration
        this.cooldowns.clear()
        this.lastCooldownToken = null
        this.restoringValue = false
        committed = true
      },
      rollback: () => {
        if (finalized) return
        this.loaderValue = previousLoader
        this.resourceGenerationValue = previousGeneration
        this.fileResources.clear()
        for (const [fileIndex, resource] of previousResources) {
          this.fileResources.set(fileIndex, resource)
          this.restoringFileResources.delete(fileIndex)
        }
        this.environmentResource = previousEnvironment
        this.restoringEnvironmentResource = null
        this.cooldowns.clear()
        for (const [fileIndex, remaining] of previousCooldowns) {
          this.cooldowns.set(fileIndex, remaining)
        }
        this.lastCooldownToken = previousToken
        this.restoringValue = false
        try {
          if (loader !== previousLoader) loader.destroy()
        } finally {
          finalized = true
        }
      },
      finalize: () => {
        if (finalized || !committed) return
        finalized = true
        if (previousLoader && previousLoader !== loader) previousLoader.destroy()
        this.restoringEnvironmentResource = null
        this.pruneRestoringResources()
      },
    }
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.restoringValue = false
    this.fileResources.clear()
    this.restoringFileResources.clear()
    this.environmentResource = null
    this.restoringEnvironmentResource = null
    this.cooldowns.clear()
    this.lastCooldownToken = null
    try {
      this.loaderValue?.destroy()
    } finally {
      this.loaderValue = null
    }
  }

  dispose(): void {
    this.destroy()
  }

  private tryUnloadResource(fileIndex: number, force = false): boolean {
    if (!this.loaderValue) return true
    const resource = this.fileResources.get(fileIndex)
    const restoringResource = this.restoringFileResources.get(fileIndex)
    if (
      !force &&
      (this.fileRefCounts[fileIndex] !== 0 ||
        this.fileLoadInterests[fileIndex] !== 0 ||
        (resource?.refCount ?? 0) !== 0 ||
        (restoringResource?.refCount ?? 0) !== 0)
    ) {
      return false
    }
    this.loaderValue.unload(this.files[fileIndex].url)
    const changed = this.fileResources.delete(fileIndex)
    this.cooldowns.delete(fileIndex)
    if (changed) this.traceLodCounts()
    return true
  }

  private isCurrentGeneration(resource: TResource): boolean {
    return (
      this.resourceGenerationValue === undefined ||
      resource.generation === undefined ||
      resource.generation === this.resourceGenerationValue
    )
  }

  private pruneRestoringResources(): void {
    for (const [fileIndex, resource] of this.restoringFileResources) {
      if ((resource.refCount ?? 0) === 0) this.restoringFileResources.delete(fileIndex)
    }
  }

  private traceLodCounts(): void {
    if (!this.traceLodCountsCallback) return
    const counts = new Array<number>(this.lodLevels).fill(0)
    for (const fileIndex of this.fileResources.keys()) {
      const lodLevel = this.files[fileIndex]?.lodLevel ?? -1
      if (lodLevel >= 0 && lodLevel < counts.length) counts[lodLevel]++
    }
    try {
      this.traceLodCountsCallback(counts)
    } catch (cause) {
      log.warn(`GSplatOctree trace 回调失败: ${cause instanceof Error ? cause.message : cause}`)
    }
  }

  private extractLeaves(
    node: GSplatOctreeManifestNode,
    result: Array<{
      lods: Readonly<Record<string, GSplatOctreeManifestLod>>
      bound: GSplatOctreeBoundData
    }>
  ): void {
    if (node.lods) {
      if (!node.bound) throw new Error('GSplatOctree leaf 缺少 bound')
      validateBound(node.bound)
      result.push({ lods: node.lods, bound: node.bound })
      return
    }
    for (const child of node.children ?? []) this.extractLeaves(child, result)
  }

  private validateManifestLod(
    lod: GSplatOctreeManifestLod,
    nodeIndex: number,
    lodIndex: number
  ): void {
    if (!Number.isSafeInteger(lod.file) || lod.file < 0 || lod.file >= this.files.length) {
      throw new Error(
        `非法 GSplatOctree file index: node=${nodeIndex}, lod=${lodIndex}, file=${lod.file}`
      )
    }
    for (const [label, value] of [
      ['offset', lod.offset ?? 0],
      ['count', lod.count ?? 0],
    ] as const) {
      if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`非法 GSplatOctree ${label}: node=${nodeIndex}, lod=${lodIndex}`)
      }
    }
  }

  private createFlatBounds(nodes: readonly GSplatOctreeNode[]): Float32Array {
    const result = new Float32Array(nodes.length * 6)
    for (let index = 0; index < nodes.length; index++) {
      const { min, max } = nodes[index].bounds
      result.set([min.x, min.y, min.z, max.x, max.y, max.z], index * 6)
    }
    return result
  }

  private assertFileIndex(fileIndex: number): void {
    if (!Number.isSafeInteger(fileIndex) || fileIndex < 0 || fileIndex >= this.files.length) {
      throw new Error(`非法 GSplatOctree fileIndex: ${fileIndex}`)
    }
  }

  private validateCooldown(value: number): number {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error(`非法 GSplatOctree cooldownTicks: ${value}`)
    }
    return value
  }

  private validateGeneration(value: number | undefined): number | undefined {
    if (value !== undefined && (!Number.isSafeInteger(value) || value < 0)) {
      throw new Error(`非法 GSplatOctree resourceGeneration: ${value}`)
    }
    return value
  }
}

function validateBound(bound: GSplatOctreeBoundData): void {
  for (const value of [...bound.min, ...bound.max]) {
    if (!Number.isFinite(value)) throw new Error('GSplatOctree bound 必须为有限数')
  }
  for (let axis = 0; axis < 3; axis++) {
    if (bound.min[axis] > bound.max[axis]) throw new Error('GSplatOctree bound min 不能大于 max')
  }
}

function isRelativeUrl(url: string): boolean {
  return !/^[a-z][a-z\d+.-]*:/i.test(url) && !url.startsWith('//') && !url.startsWith('/')
}

export function resolveBaseUrl(filePath: string): string {
  const queryPathMatch = filePath.match(/^([^?]+\?path=)(.*)$/)
  if (queryPathMatch) {
    const decoded = safeDecode(queryPathMatch[2])
    const slash = Math.max(decoded.lastIndexOf('/'), decoded.lastIndexOf('\\'))
    if (slash < 0) return queryPathMatch[1]
    const directory = decoded.slice(0, slash + 1)
    return (
      queryPathMatch[1] +
      (queryPathMatch[2].includes('%') ? encodeURIComponent(directory) : directory)
    )
  }

  const localFileMatch = filePath.match(/^(https?:\/\/[^/]+\/local-file\/)(.+)$/)
  if (localFileMatch) {
    const decoded = safeDecode(localFileMatch[2])
    const slash = Math.max(decoded.lastIndexOf('/'), decoded.lastIndexOf('\\'))
    return slash < 0
      ? localFileMatch[1]
      : localFileMatch[1] + encodeURIComponent(decoded.slice(0, slash + 1))
  }

  const clean = filePath.split(/[?#]/, 1)[0]
  const slash = Math.max(clean.lastIndexOf('/'), clean.lastIndexOf('\\'))
  return slash < 0 ? '' : clean.slice(0, slash + 1)
}

export function normalizeShardDir(shard: string, baseDir: string): string {
  try {
    const shardUrl = new URL(shard)
    if (shardUrl.pathname !== '/' || shardUrl.search || shardUrl.hash) return shard
    try {
      const baseUrl = new URL(baseDir)
      return shardUrl.origin + baseDir.slice(baseUrl.origin.length)
    } catch {
      return shardUrl.origin + (baseDir.startsWith('/') ? baseDir : `/${baseDir}`)
    }
  } catch {
    return shard
  }
}

function joinPath(base: string, relative: string): string {
  const queryPathMatch = base.match(/^([^?]+\?path=)(.*)$/)
  if (queryPathMatch) {
    const decoded = safeDecode(queryPathMatch[2])
    const separator = decoded.includes('\\') ? '\\' : '/'
    const normalizedBase = /[/\\]$/.test(decoded) ? decoded : `${decoded}${separator}`
    const fullPath = normalizedBase + relative.replace(/[/\\]/g, separator)
    return (
      queryPathMatch[1] +
      (queryPathMatch[2].includes('%') ? encodeURIComponent(fullPath) : fullPath)
    )
  }

  const localFileMatch = base.match(/^(https?:\/\/[^/]+\/local-file\/)(.+)$/)
  if (localFileMatch) {
    const decoded = safeDecode(localFileMatch[2])
    const directory = /[/\\]$/.test(decoded) ? decoded : `${decoded}/`
    return localFileMatch[1] + encodeURIComponent(directory + relative)
  }

  return `${base.replace(/\/$/, '')}/${relative.replace(/^\//, '')}`
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
